import React, { useState, useEffect, useCallback } from 'react';
import {
  Wallet,
  Clock,
  CheckCircle,
  Loader2,
  ArrowLeft,
  Send,
  XCircle,
  Pencil,
  X,
  Check,
  AlertTriangle,
  Users,
  Filter,
} from 'lucide-react';
import type { Team } from '../../types';
import {
  getPayoutSummary,
  getPayableAffiliates,
  createPayout,
  getPayouts,
  getPayoutDetail,
  confirmPayout,
  failPayout,
  updateAffiliatePayoutDetails,
  formatUSD,
  type PayoutSummary,
  type PayableAffiliate,
  type PartnerPayout,
  type PayoutDetail,
} from '../../services/payouts';

interface AdminPayoutsPageProps {
  team: Team;
  userId: string;
}

const AdminPayoutsPage: React.FC<AdminPayoutsPageProps> = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<PayoutSummary | null>(null);
  const [payableAffiliates, setPayableAffiliates] = useState<PayableAffiliate[]>([]);
  const [payouts, setPayouts] = useState<PartnerPayout[]>([]);
  const [activeTab, setActiveTab] = useState<'ready' | 'history'>('ready');
  const [historyFilter, setHistoryFilter] = useState<string>('');

  const [detail, setDetail] = useState<PayoutDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [payoutReference, setPayoutReference] = useState('');
  const [confirmingPayout, setConfirmingPayout] = useState(false);
  const [failingPayout, setFailingPayout] = useState(false);

  const [creatingFor, setCreatingFor] = useState<string | null>(null);

  const [editingAffiliate, setEditingAffiliate] = useState<PayableAffiliate | null>(null);
  const [editPaypal, setEditPaypal] = useState('');
  const [editWise, setEditWise] = useState('');
  const [editMethod, setEditMethod] = useState<'paypal' | 'wise'>('paypal');
  const [savingDetails, setSavingDetails] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [summaryData, affiliateData, payoutData] = await Promise.all([
      getPayoutSummary(),
      getPayableAffiliates(),
      getPayouts(historyFilter || undefined),
    ]);
    setSummary(summaryData);
    setPayableAffiliates(affiliateData);
    setPayouts(payoutData);
    setLoading(false);
  }, [historyFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreatePayout = async (affiliateId: string) => {
    setCreatingFor(affiliateId);
    const result = await createPayout(affiliateId);
    setCreatingFor(null);
    if (result.success && result.payout_id) {
      handleViewPayout(result.payout_id);
      fetchData();
    } else {
      alert(result.error || 'Failed to create payout');
    }
  };

  const handleViewPayout = async (payoutId: string) => {
    setLoadingDetail(true);
    const data = await getPayoutDetail(payoutId);
    setDetail(data);
    setPayoutReference(data?.payout?.payout_reference || '');
    setLoadingDetail(false);
  };

  const handleConfirmPayout = async () => {
    if (!detail) return;
    setConfirmingPayout(true);
    const result = await confirmPayout(detail.payout.id, payoutReference || undefined);
    setConfirmingPayout(false);
    if (result.success) {
      handleViewPayout(detail.payout.id);
      fetchData();
    } else {
      alert(result.error || 'Failed to confirm payout');
    }
  };

  const handleFailPayout = async () => {
    if (!detail) return;
    if (!confirm('Are you sure? This will reset all commissions back to pending.')) return;
    setFailingPayout(true);
    const result = await failPayout(detail.payout.id);
    setFailingPayout(false);
    if (result.success) {
      setDetail(null);
      fetchData();
    } else {
      alert(result.error || 'Failed to mark payout as failed');
    }
  };

  const handleEditDetails = (affiliate: PayableAffiliate) => {
    setEditingAffiliate(affiliate);
    setEditPaypal(affiliate.paypal_email || '');
    setEditWise(affiliate.wise_email || '');
    setEditMethod((affiliate.preferred_payout_method as 'paypal' | 'wise') || 'paypal');
  };

  const handleSaveDetails = async () => {
    if (!editingAffiliate) return;
    setSavingDetails(true);
    const result = await updateAffiliatePayoutDetails(
      editingAffiliate.id,
      editPaypal || null,
      editWise || null,
      editMethod
    );
    setSavingDetails(false);
    if (result.success) {
      setEditingAffiliate(null);
      fetchData();
    } else {
      alert(result.error || 'Failed to update payout details');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-500/20 text-amber-400 rounded-full">
            <Clock size={10} /> Pending
          </span>
        );
      case 'sent':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-blue-500/20 text-blue-400 rounded-full">
            <Send size={10} /> Sent
          </span>
        );
      case 'paid':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-emerald-500/20 text-emerald-400 rounded-full">
            <CheckCircle size={10} /> Paid
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-red-500/20 text-red-400 rounded-full">
            <XCircle size={10} /> Failed
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  // ── Detail View ──────────────────────────────────────────────
  if (detail || loadingDetail) {
    if (loadingDetail) {
      return (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      );
    }
    if (!detail) return null;

    const { payout, commissions } = detail;
    const payoutEmail =
      payout.payout_method === 'paypal' ? payout.paypal_email : payout.wise_email;

    return (
      <div className="p-6 space-y-6">
        <button
          onClick={() => setDetail(null)}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Payouts
        </button>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Wallet className="w-6 h-6 text-orange-500" />
              Payout Details
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              {payout.affiliate_email} &middot; {payout.affiliate_code}
            </p>
          </div>
          {getStatusBadge(payout.status)}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
            <span className="text-xs text-slate-400">Amount</span>
            <p className="text-xl font-bold text-white mt-1">{formatUSD(payout.amount_cents)}</p>
          </div>
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
            <span className="text-xs text-slate-400">Method</span>
            <p className="text-xl font-bold text-white mt-1 capitalize">{payout.payout_method}</p>
          </div>
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
            <span className="text-xs text-slate-400">Send To</span>
            <p className="text-sm font-medium text-white mt-1 truncate">
              {payoutEmail || <span className="text-slate-500 italic">Not set</span>}
            </p>
          </div>
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
            <span className="text-xs text-slate-400">Created</span>
            <p className="text-sm font-medium text-white mt-1">
              {new Date(payout.created_at).toLocaleDateString()}
            </p>
            {payout.paid_at && (
              <p className="text-xs text-emerald-400 mt-1">
                Paid: {new Date(payout.paid_at).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>

        {payout.payout_reference && (
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
            <span className="text-xs text-slate-400">Payment Reference</span>
            <p className="text-sm text-white mt-1 font-mono">{payout.payout_reference}</p>
          </div>
        )}

        <div>
          <h3 className="text-sm font-medium text-white mb-3">
            Commission Breakdown ({commissions.length})
          </h3>
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700/50">
                  <th className="px-4 py-3 text-left text-slate-400 font-medium">Date</th>
                  <th className="px-4 py-3 text-left text-slate-400 font-medium">Referred User</th>
                  <th className="px-4 py-3 text-left text-slate-400 font-medium">Purchase</th>
                  <th className="px-4 py-3 text-right text-slate-400 font-medium">Purchase Amt</th>
                  <th className="px-4 py-3 text-right text-slate-400 font-medium">Commission</th>
                </tr>
              </thead>
              <tbody>
                {commissions.map((c) => (
                  <tr key={c.id} className="border-b border-slate-700/30 last:border-0">
                    <td className="px-4 py-3 text-slate-400">
                      {new Date(c.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-slate-300">{c.referred_email}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs font-mono truncate max-w-[140px]">
                      {c.purchase_id}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-300">
                      {formatUSD(c.purchase_amount_cents)}
                    </td>
                    <td className="px-4 py-3 text-right text-white font-medium">
                      {formatUSD(c.commission_amount_cents)}
                    </td>
                  </tr>
                ))}
                {commissions.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                      No commissions linked to this payout.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {payout.status !== 'paid' && payout.status !== 'failed' && (
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 space-y-4">
            <h3 className="text-sm font-medium text-white">Actions</h3>
            <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
              <div className="flex-1 w-full">
                <label className="block text-xs text-slate-400 mb-1">
                  Payment Reference (PayPal Transaction ID, Wise Transfer ID, etc.)
                </label>
                <input
                  type="text"
                  value={payoutReference}
                  onChange={(e) => setPayoutReference(e.target.value)}
                  placeholder="e.g. PP-TXN-123456 or WISE-T12345"
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm focus:outline-none focus:border-orange-500"
                />
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <button
                  onClick={handleConfirmPayout}
                  disabled={confirmingPayout}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  {confirmingPayout ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  Mark Paid
                </button>
                <button
                  onClick={handleFailPayout}
                  disabled={failingPayout}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 disabled:bg-slate-700 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  {failingPayout ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <XCircle className="w-4 h-4" />
                  )}
                  Mark Failed
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── List View ────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Wallet className="w-6 h-6 text-orange-500" />
          Partner Payouts
        </h1>
        <p className="text-slate-400 text-sm mt-1">
          Review commissions and pay partners via PayPal or Wise
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-amber-400" />
            <span className="text-sm text-slate-400">Pending Commission Total</span>
          </div>
          <p className="text-2xl font-bold text-amber-400">
            {formatUSD(summary?.total_pending_cents ?? 0)}
          </p>
        </div>
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-slate-400">Ready To Pay</span>
          </div>
          <p className="text-2xl font-bold text-blue-400">
            {summary?.ready_to_pay_count ?? 0}
          </p>
          <p className="text-xs text-slate-500 mt-1">affiliates &ge; $50</p>
        </div>
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-4 h-4 text-emerald-400" />
            <span className="text-sm text-slate-400">Paid This Month</span>
          </div>
          <p className="text-2xl font-bold text-emerald-400">
            {formatUSD(summary?.paid_this_month_cents ?? 0)}
          </p>
        </div>
      </div>

      <div className="flex gap-2 border-b border-slate-700">
        <button
          onClick={() => setActiveTab('ready')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'ready'
              ? 'border-orange-500 text-orange-400'
              : 'border-transparent text-slate-400 hover:text-slate-300'
          }`}
        >
          <Send className="w-4 h-4" />
          Ready to Pay
          {payableAffiliates.length > 0 && (
            <span className="px-1.5 py-0.5 text-xs bg-orange-500 text-white rounded-full">
              {payableAffiliates.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'history'
              ? 'border-orange-500 text-orange-400'
              : 'border-transparent text-slate-400 hover:text-slate-300'
          }`}
        >
          Payout History ({payouts.length})
        </button>
      </div>

      {activeTab === 'ready' && (
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700/50">
                <th className="px-4 py-3 text-left text-slate-400 font-medium">Affiliate</th>
                <th className="px-4 py-3 text-right text-slate-400 font-medium">Pending Amount</th>
                <th className="px-4 py-3 text-center text-slate-400 font-medium">Method</th>
                <th className="px-4 py-3 text-left text-slate-400 font-medium">PayPal Email</th>
                <th className="px-4 py-3 text-left text-slate-400 font-medium">Wise Email</th>
                <th className="px-4 py-3 text-center text-slate-400 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {payableAffiliates.map((affiliate) => (
                <tr
                  key={affiliate.id}
                  className="border-b border-slate-700/30 last:border-0 hover:bg-slate-800/30"
                >
                  <td className="px-4 py-3">
                    <div className="text-slate-300">{affiliate.email}</div>
                    <div className="text-xs text-slate-500 font-mono">{affiliate.code}</div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="text-white font-medium">
                      {formatUSD(affiliate.pending_amount_cents)}
                    </div>
                    <div className="text-xs text-slate-500">
                      {affiliate.pending_count} commission
                      {affiliate.pending_count !== 1 ? 's' : ''}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="capitalize text-slate-300">
                      {affiliate.preferred_payout_method || 'paypal'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-300 text-sm">
                    {affiliate.paypal_email || (
                      <span className="text-slate-500 italic">Not set</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-300 text-sm">
                    {affiliate.wise_email || (
                      <span className="text-slate-500 italic">Not set</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => handleEditDetails(affiliate)}
                        className="p-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors"
                        title="Edit payout details"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleCreatePayout(affiliate.id)}
                        disabled={creatingFor === affiliate.id}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-600 hover:bg-orange-500 disabled:bg-slate-700 text-white rounded-lg text-sm font-medium transition-colors"
                      >
                        {creatingFor === affiliate.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Send className="w-3.5 h-3.5" />
                        )}
                        Create Payout
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {payableAffiliates.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    <AlertTriangle className="w-5 h-5 mx-auto mb-2 text-slate-600" />
                    No affiliates ready for payout. Minimum threshold: $50.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-slate-800 rounded-lg p-1">
              <Filter className="w-4 h-4 text-slate-400 ml-2" />
              <select
                value={historyFilter}
                onChange={(e) => setHistoryFilter(e.target.value)}
                className="bg-transparent text-sm text-slate-300 border-0 focus:outline-none cursor-pointer pr-2"
              >
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="sent">Sent</option>
                <option value="paid">Paid</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          </div>

          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700/50">
                  <th className="px-4 py-3 text-left text-slate-400 font-medium">Date</th>
                  <th className="px-4 py-3 text-left text-slate-400 font-medium">Affiliate</th>
                  <th className="px-4 py-3 text-right text-slate-400 font-medium">Amount</th>
                  <th className="px-4 py-3 text-center text-slate-400 font-medium">Method</th>
                  <th className="px-4 py-3 text-left text-slate-400 font-medium">Reference</th>
                  <th className="px-4 py-3 text-center text-slate-400 font-medium">Status</th>
                  <th className="px-4 py-3 text-center text-slate-400 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {payouts.map((payout) => (
                  <tr
                    key={payout.id}
                    className="border-b border-slate-700/30 last:border-0 hover:bg-slate-800/30"
                  >
                    <td className="px-4 py-3 text-slate-400">
                      {new Date(payout.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-slate-300">{payout.affiliate_email}</div>
                      <div className="text-xs text-slate-500 font-mono">
                        {payout.affiliate_code}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-white font-medium">
                      {formatUSD(payout.amount_cents)}
                    </td>
                    <td className="px-4 py-3 text-center text-slate-300 capitalize">
                      {payout.payout_method}
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-xs font-mono">
                      {payout.payout_reference || '-'}
                    </td>
                    <td className="px-4 py-3 text-center">{getStatusBadge(payout.status)}</td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleViewPayout(payout.id)}
                        className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-xs font-medium transition-colors"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
                {payouts.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                      No payouts found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editingAffiliate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Edit Payout Details</h3>
              <button
                onClick={() => setEditingAffiliate(null)}
                className="p-1 text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-400 mb-4">
              {editingAffiliate.email} &middot; {editingAffiliate.code}
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-xs text-slate-400 mb-1">PayPal Email</label>
                <input
                  type="email"
                  value={editPaypal}
                  onChange={(e) => setEditPaypal(e.target.value)}
                  placeholder="partner@paypal.com"
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm focus:outline-none focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Wise Email</label>
                <input
                  type="email"
                  value={editWise}
                  onChange={(e) => setEditWise(e.target.value)}
                  placeholder="partner@wise.com"
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm focus:outline-none focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Preferred Method</label>
                <select
                  value={editMethod}
                  onChange={(e) => setEditMethod(e.target.value as 'paypal' | 'wise')}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-orange-500"
                >
                  <option value="paypal">PayPal</option>
                  <option value="wise">Wise</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setEditingAffiliate(null)}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveDetails}
                disabled={savingDetails}
                className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-500 disabled:bg-slate-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                {savingDetails ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPayoutsPage;
